import { Cell } from '@/grid/Cell';
import { Cluster } from './Cluster';

export interface CacheEntry {
  path: Cell[];
  cost: number;
}

export class IntraClusterCache {
  private cache: Map<string, CacheEntry> = new Map();
  private maxSize: number;
  private hits: number = 0;
  private misses: number = 0;
  private accessOrder: string[] = [];

  constructor(maxSize: number = 1000) {
    this.maxSize = maxSize;
  }

  private generateKey(cluster: Cluster, start: Cell, goal: Cell): string {
    return `${cluster.id}_${start.row}_${start.col}_${goal.row}_${goal.col}`;
  }

  get(cluster: Cluster, start: Cell, goal: Cell): CacheEntry | null {
    const key = this.generateKey(cluster, start, goal);
    const entry = this.cache.get(key);

    if (entry) {
      this.hits++;
      this.accessOrder = this.accessOrder.filter(k => k !== key);
      this.accessOrder.push(key);
      return entry;
    }

    this.misses++;
    return null;
  }

  set(cluster: Cluster, start: Cell, goal: Cell, path: Cell[], cost: number): void {
    const key = this.generateKey(cluster, start, goal);
    const entry: CacheEntry = { path, cost };

    if (this.cache.size >= this.maxSize && !this.cache.has(key)) {
      const oldestKey = this.accessOrder.shift();
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(key, entry);
    this.accessOrder.push(key);
  }

  invalidateCluster(cluster: Cluster): void {
    const keysToDelete: string[] = [];
    for (const key of this.cache.keys()) {
      if (key.startsWith(cluster.id + '_')) {
        keysToDelete.push(key);
      }
    }
    keysToDelete.forEach(key => {
      this.cache.delete(key);
      this.accessOrder = this.accessOrder.filter(k => k !== key);
    });
  }

  clear(): void {
    this.cache.clear();
    this.accessOrder = [];
    this.hits = 0;
    this.misses = 0;
  }

  getStats(): {
    hits: number;
    misses: number;
    size: number;
    hitRate: number;
  } {
    const total = this.hits + this.misses;
    const hitRate = total > 0 ? this.hits / total : 0;

    return {
      hits: this.hits,
      misses: this.misses,
      size: this.cache.size,
      hitRate
    };
  }
}
